import { roundRobinCDN } from '@core/helpers/video'
import { isVideoHorizontal } from '@core/helpers/video/isVideoHorizontal'
import type { CDNOptionsType } from '@core/helpers/video/roundRobinCDN'
import type { Api_Video, Api_Video_VideoFile } from '@src/core/api'

const FORMAT_TO_TYPE = {
  h264: 'video/mp4',
  hevc: 'video/mp4',
}

const FORMAT_TO_CODECS = {
  h264: 'avc1.42E01E',
  hevc: 'hvc1',
}

const DEFAULT_TRAILER = 'default_event_trailer'

const isDefaultTrailer = (file: Api_Video_VideoFile) =>
  file.file_url.includes(DEFAULT_TRAILER)

/**
 * Return default trailer files if only those are present or filter video files which
 * match video orientation (custom trailer).
 */
function filterVideoFilesMatchingOrientation(video: Api_Video) {
  const videoFiles = video.video_files || []

  if (videoFiles.every(isDefaultTrailer)) {
    return videoFiles.filter((file) => !file.has_watermark)
  }

  return videoFiles.filter((file) => {
    if (
      !file.has_watermark &&
      !isDefaultTrailer(file) &&
      ((isVideoHorizontal(video) && file.video_file_type === 'horizontal') ||
        (!isVideoHorizontal(video) && file.video_file_type === 'vertical'))
    ) {
      return true
    }

    return false
  })
}

/**
 * Pick relevant files from video.video_files based on video orientation
 * or/and attach `download_url` file as a fallback.
 */
export const orderVideoFiles = (
  video: Api_Video,
  preferredCDNs?: Array<CDNOptionsType>,
) => {
  const videoFiles = video.video_files || []

  const orderedFiles = filterVideoFilesMatchingOrientation(video).map(
    (file) => ({
      src: roundRobinCDN(file.file_url, preferredCDNs),
      type: FORMAT_TO_TYPE[file.format || ''] || null,
      codecs: FORMAT_TO_CODECS[file.format || ''] || null,
      placeholder_blur_hash: file.placeholder_blur_hash || null,
      placeholder_url: file.placeholder_url
        ? roundRobinCDN(file.placeholder_url, preferredCDNs)
        : null,
    }),
  )

  // If there are no `video_files` matching orientation, or no h264 file...
  // add the `download_url` file as a fallback.
  if (
    !orderedFiles.some((file) => file.type === FORMAT_TO_CODECS['h264']) &&
    video.download_url
  ) {
    const fromVideoFiles = videoFiles.find(
      (file) => video.download_url === file.file_url,
    )

    orderedFiles.push({
      src: roundRobinCDN(video.download_url, preferredCDNs),
      type: FORMAT_TO_TYPE['h264'],
      codecs: null,
      placeholder_blur_hash: fromVideoFiles?.placeholder_blur_hash || null,
      placeholder_url: fromVideoFiles?.placeholder_url
        ? roundRobinCDN(fromVideoFiles?.placeholder_url, preferredCDNs)
        : null,
    })
  }

  return orderedFiles
}
